import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useAccountContext } from 'contexts/account';

export const useAccountPlans = (page, type) => {
  const { selectedAccountId } = useAccountContext();
  const { data, loading, error, refetch, networkStatus } = useQuery<
    SearchPlansForPlanDetailsQuery,
    SearchPlansForPlanDetailsQueryVariables
  >(FETCH_PLANS_OF_TYPE, {
    variables: { accountId: selectedAccountId, page: page + 1, type },
    notifyOnNetworkStatusChange: true,
  });

  const accountPlansData = useMemo(() => {
    if (!data) return undefined;
    return data.search.plans;
  }, [data, type, page]);

  return {
    data: accountPlansData,
    error,
    loading,
    refetch,
    networkStatus,
  };
};

export const FETCH_PLANS_OF_TYPE = gql`
  query searchPlansForPlanDetails(
    $accountId: Int!
    $page: Int!
    $type: String!
  ) {
    search {
      plans(
        page: $page
        limit: 20
        sort: created_at
        order: ASC
        filters: { accountId: $accountId, statuses: [$type] }
      ) {
        count
        plans {
          id
          displayName
          active
          name
          accountNumber
          activeMembershipProductSubscription {
            id
            amountCents
          }
          isExclusiveMembershipPlan
          reactivationEligibility {
            eligible
            reasons
          }
          productStatuses {
            billPay {
              status
            }
          }
          utilityAccounts {
            id
            displayServiceAddress
            serviceAddressZip
            accountNumber
            gasOnly
            utilityAccountPersonalizedContracts {
              id
              utilityAccountId
              contractVersionId
              contractAgreementId
            }
            utility {
              id
              displayName
              state {
                id
                abbreviation
                medicaidCardRequiresThirdPartyVerification
              }
            }
          }
        }
      }
    }
  }
`;

export const accountPlansMock = {
  accountNumber: 1111,
  active: true,
  displayName: 'Home',
  id: 3,
  name: 'My plan',
  activeMembershipProductSubscription: {
    id: 1,
    amountCents: 500,
  },
  isExclusiveMembershipPlan: true,
  productStatuses: {
    billPay: {
      status: 'active',
    },
  },
  utilityAccounts: [
    {
      id: 1,
      displayServiceAddress: '1234 Fake St',
      accountNumber: 11234,
      utility: {
        id: 1,
        displayName: 'Fake Utility',
        state: {
          id: 1,
          abbreviation: 'VA',
        },
      },
    },
  ],
};

export const maAccountPlansMock = {
  accountNumber: 1111,
  active: true,
  displayName: 'Home',
  id: 3,
  name: 'My plan',
  activeMembershipProductSubscription: {
    id: 1,
    amountCents: 500,
  },
  isExclusiveMembershipPlan: true,
  productStatuses: {
    billPay: {
      status: 'active',
    },
  },
  utilityAccounts: [
    {
      id: 1,
      displayServiceAddress: '1234 Fake St',
      accountNumber: 11234,
      utility: {
        id: 1,
        displayName: 'Fake Utility',
        state: {
          id: 1,
          abbreviation: 'MA',
        },
      },
      utilityAccountPersonalizedContracts: [
        {
          id: 1,
          utilityAccountId: 1,
          contractVersionId: 1,
          contractAgreementId: 1,
        },
      ],
    },
  ],
};

export const mnAccountPlansMock = {
  accountNumber: 1111,
  active: true,
  displayName: 'Home',
  id: 3,
  name: 'My plan',
  activeMembershipProductSubscription: {
    id: 1,
    amountCents: 500,
  },
  isExclusiveMembershipPlan: true,
  productStatuses: {
    billPay: {
      status: 'active',
    },
  },
  utilityAccounts: [
    {
      id: 1,
      displayServiceAddress: '1234 Fake St',
      accountNumber: 11234,
      utility: {
        id: 1,
        displayName: 'Fake Utility',
        state: {
          id: 1,
          abbreviation: 'MN',
        },
      },
    },
  ],
};

export const useAccountPlansMock = {
  plans: {
    plans: [accountPlansMock],
    count: 1,
  },
  loading: true,
  refetch: () => null,
  error: null,
};

export const useMaAccountPlansMock = {
  plans: {
    plans: [maAccountPlansMock],
    count: 1,
  },
  loading: true,
  refetch: () => null,
  error: null,
};

export const useMnAccountPlansMock = {
  plans: {
    plans: [mnAccountPlansMock],
    count: 1,
  },
  loading: true,
  refetch: () => null,
  error: null,
};

export interface AccountPlan {
  id: number;
  displayName: string;
  active: boolean;
  name: string;
  accountNumber: number;
  activeMembershipProductSubscription: {
    id: number;
    amountCents: number;
  };
  isExclusiveMembershipPlan: boolean;
  reactivationEligibility: {
    eligible: boolean;
    reasons: string[];
  };
  productStatuses: {
    billPay: {
      status: string;
    };
  };
  utilityAccounts: {
    id: number;
    displayServiceAddress: string;
    serviceAddressZip: string | null;
    accountNumber: string | null;
    gasOnly: boolean;
    utilityAccountPersonalizedContracts: Array<{
      id: number | null;
      utilityAccountId: number | null;
      contractVersionId: number | null;
      contractAgreementId: number | null;
    } | null> | null;
    utility: {
      id: number;
      displayName: string;
      state: {
        id: number;
        abbreviation: string;
        medicaidCardRequiresThirdPartyVerification: boolean;
      };
    };
  }[];
}
